/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, May 13th 2022, 12:12:32 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Grid from "@mui/material/Grid";
import { CHOOSE_SUBSCRIPTION_SUBTITLE } from "config";
import { resolveURLName } from "core/routes/utils";
import { ChoiceIcon } from "theme/icons";
import { useSubscriptionContext } from "saas-subscription/providers";
import { SubscriptionPlans } from "saas-subscription/components/SubscriptionPlans";
import { EmphasisText, OSMarketingBanner, Image, OSActionButton, FullScreenNotificationGrid } from "ui-widgets";

export const SubscriptionPendingCTA = ({ organizationName }) => {
  const { subscriptionTracker, isFetchingSubscriptionData } = useSubscriptionContext();

  if (subscriptionTracker?.status === "unknown" || isFetchingSubscriptionData) {
    return <ProcessingSubscriptionRequestNotification organizationName={organizationName} />;
  }

  return <ChooseSubscriptionCTA organizationName={organizationName} />;
};

const ChooseSubscriptionCTA = ({ organizationName }: React.ComponentProps<typeof SubscriptionPendingCTA>) => {
  return (
    <FullScreenNotificationGrid
      pageTitle={
        <div className="os-marketing-banner-dashboard">
          <OSMarketingBanner
            title={
              <>
                Get Started for absolutely <EmphasisText>free</EmphasisText>!
              </>
            }
            subTitle={<>{CHOOSE_SUBSCRIPTION_SUBTITLE}.</>}
          />
        </div>
      }
      pageContent={<p>Kindly choose a subscription plan below to activate {organizationName} on Onestop.</p>}
      pageIllustration={<ChoiceIcon height={100} width={100} />}
      addtionalGridItems={
        <Grid item xs={12}>
          <SubscriptionPlans />
        </Grid>
      }
    />
  );
};

const ProcessingSubscriptionRequestNotification = ({
  organizationName,
}: React.ComponentProps<typeof SubscriptionPendingCTA>) => {
  return (
    <FullScreenNotificationGrid
      pageTitle={
        <div className="os-marketing-banner-dashboard">
          <OSMarketingBanner
            title={<>Subscription Request Received!</>}
            subTitle={
              <>
                We are currently processing your subscription request for{" "}
                <EmphasisText>{organizationName}</EmphasisText> and it seems to be taking a bit of time. Sorry for the
                delay. Kindly check after a few minutes or refresh the page.
              </>
            }
          />
        </div>
      }
      pageContent={
        <a href={resolveURLName("dashboard")}>
          <OSActionButton size="large" variant="contained">
            Refresh
          </OSActionButton>
        </a>
      }
      pageIllustration={<Image height={100} width={100} src="/img/in-progress.png" />}
    />
  );
};
